import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import t from '../theme';

function Section({ title, items, loading, onAdd, onEdit, onDelete, addLabel, itemName }) {
  const [editItem, setEditItem] = useState(null);
  const [addOpen, setAddOpen]   = useState(false);
  const [name, setName]         = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  const openEdit = (item) => { setEditItem(item); setName(item.name); setError(''); setAddOpen(false); };
  const openAdd  = () => { setEditItem(null); setName(''); setError(''); setAddOpen(true); };
  const close    = () => { setEditItem(null); setAddOpen(false); setError(''); };

  const handleSave = async () => {
    if (!name.trim()) return setError(`${itemName} name is required.`);
    setSaving(true);
    try {
      if (editItem) await onEdit(editItem.id, name.trim());
      else await onAdd(name.trim());
      close();
    } catch (e) { setError(e.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ ...t.card, padding: '0', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: t.text, margin: 0 }}>{title}</h3>
          <p style={{ fontSize: '12px', color: t.text3, margin: '2px 0 0' }}>{items.length} {itemName.toLowerCase()}s</p>
        </div>
        <button style={t.btnPrimary} onClick={openAdd}>+ Add</button>
      </div>

      {(addOpen || editItem) && (
        <div style={{ padding: '16px 20px', background: t.surface2, borderBottom: `1px solid ${t.border}`, display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={t.label}>{editItem ? `Edit ${itemName}` : `New ${itemName}`}</label>
            <input style={t.input} value={name} onChange={e => setName(e.target.value)}
              placeholder={`${itemName} name...`} onKeyDown={e => e.key === 'Enter' && handleSave()} autoFocus />
            {error && <div style={{ fontSize: '12px', color: t.red }}>{error}</div>}
          </div>
          <div style={{ display: 'flex', gap: '8px', paddingTop: '22px' }}>
            <button style={t.btnSecondary} onClick={close}>Cancel</button>
            <button style={t.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? '...' : 'Save'}</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: '32px', textAlign: 'center', color: t.text3 }}>Loading...</div>
        : items.length === 0 ? <div style={{ padding: '32px', textAlign: 'center', color: t.text3 }}>No {itemName.toLowerCase()}s yet.</div>
        : <div>
          {items.map((item, i) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', borderBottom: i < items.length - 1 ? `1px solid ${t.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: t.accentLight,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: t.accent }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: '14px', fontWeight: 500, color: t.text }}>{item.name}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button style={{ ...t.btnSecondary, padding: '5px 12px', fontSize: '12px' }} onClick={() => openEdit(item)}>Edit</button>
                <button style={{ ...t.btnDanger, padding: '5px 12px', fontSize: '12px' }} onClick={() => onDelete(item.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}

export default function TaalsTypesPage() {
  const [taals, setTaals] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingT, setLoadingT] = useState(true);
  const [loadingTy, setLoadingTy] = useState(true);

  const fetchTaals = useCallback(async () => { try { const r = await axios.get('/api/taals'); setTaals(r.data); } catch(e){} finally { setLoadingT(false); } }, []);
  const fetchTypes = useCallback(async () => { try { const r = await axios.get('/api/types'); setTypes(r.data); } catch(e){} finally { setLoadingTy(false); } }, []);
  useEffect(() => { fetchTaals(); fetchTypes(); }, [fetchTaals, fetchTypes]);

  const addTaal    = async name => { await axios.post('/api/taals', { name }); fetchTaals(); };
  const editTaal   = async (id, name) => { await axios.put(`/api/taals/${id}`, { name }); fetchTaals(); };
  const deleteTaal = async id => { if (!window.confirm('Delete this taal?')) return; await axios.delete(`/api/taals/${id}`); fetchTaals(); };
  const addType    = async name => { await axios.post('/api/types', { name }); fetchTypes(); };
  const editType   = async (id, name) => { await axios.put(`/api/types/${id}`, { name }); fetchTypes(); };
  const deleteType = async id => { if (!window.confirm('Delete this type?')) return; await axios.delete(`/api/types/${id}`); fetchTypes(); };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 600, color: t.text, letterSpacing: '-0.3px', margin: 0 }}>Taals & Types</h2>
        <p style={{ fontSize: '13px', color: t.text3, margin: '4px 0 0' }}>Manage the taals and lesson types used across all classes and lessons.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <Section title="Taals" items={taals} loading={loadingT} onAdd={addTaal} onEdit={editTaal} onDelete={deleteTaal} addLabel="Add Taal" itemName="Taal" />
        <Section title="Lesson Types" items={types} loading={loadingTy} onAdd={addType} onEdit={editType} onDelete={deleteType} addLabel="Add Type" itemName="Type" />
      </div>
    </div>
  );
}
